import { api } from "./client";

/**
 * Lamination line API — shared by Lamination 1 and Lamination 2.
 *
 * Both lines run the same TIA project on the same PLC family, and their
 * workers write the same three tables under a per-line prefix, so the backend
 * serves them from one router factory (`app/routers/lam_line.py`) mounted at
 * `/lam1` and `/lam2`. This is the client half of that: `makeLamApi(prefix)`
 * returns the calls for one line, and `lam1.ts` / `lam2.ts` are the two
 * instances.
 *
 * Unlike the legacy blender-backed Lamination 1 endpoints, every row carries
 * BOTH extruder A and B columns; the dashboard picks the side.
 *
 * Timestamps come back as Nashville-local naive ISO strings. Raw rows use
 * `timestamp`; period-grouped rows (group_by set) use `period`.
 */

export type LamGroupBy = "minute" | "hour" | "day" | "week" | "month";

export type LamSpeedRow = {
  timestamp?: string;
  period?: string;
  screw_speed_a: number | null;
  screw_speed_b: number | null;
};

export type LamCurrentRow = {
  timestamp?: string;
  period?: string;
  motor_current_a: number | null;
  motor_current_b: number | null;
};

export type LamStatusRow = {
  timestamp: string;
  extruder_a_position: boolean | null;
  extruder_b_position: boolean | null;
  winder_running: boolean | null;
  densifier_status: boolean | null;
};

export type LamCurrentStatus = {
  timestamp: string;
  plc: string;
  /** Seconds since the line's worker last completed a poll (its heartbeat). */
  seconds_since_read?: number | null;
  /** No completed poll inside the server's cutoff: the PLC is unreachable. */
  stale?: boolean;
  screw_speed_a: number | null;
  screw_speed_b: number | null;
  motor_current_a: number | null;
  motor_current_b: number | null;
  extruder_a_position: boolean | null;
  extruder_b_position: boolean | null;
  winder_running: boolean | null;
  densifier_status: boolean | null;
  heater_fault_a: boolean[] | null;
  heater_fault_b: boolean[] | null;
  densifier_fault: boolean[] | null;
  /** 1-based indices of tripped heater fault bits on extruder A. */
  heater_fault_a_tripped: number[];
  /** 1-based indices of tripped densifier fault bits. */
  densifier_fault_tripped: number[];
};

/**
 * Per-shift roll counts from the PLC's "Roll Analytics" buffer (DB46).
 *
 * The PLC classifies each roll itself and counts A/B/F per shift — unlike the
 * legacy Lamination 1 roller feed, where A/B/F came from length thresholds.
 * Rows come back oldest-first, ready to chart.
 */
export type LamShiftRollsRow = {
  /** PLC id: YYMMDD + shift start hour, e.g. 26072706. */
  shift_id: number;
  /** Shift start as a Nashville-local naive ISO string. */
  shift_start: string;
  shift_hour: number;
  /** Name from settings_shifts (line prefix stripped), else "06:00". */
  shift_name: string;
  a_rolls: number;
  b_rolls: number;
  f_rolls: number;
  total: number;
  updated_at: string | null;
  /** True for the newest shift while it is still running. */
  is_partial: boolean;
};

/**
 * One roll, at the time the PLC recorded it finishing.
 *
 * The PLC keeps a 100-slot ring of per-roll timestamps in DB46, which the
 * worker mirrors — so these times are the machine's own, not inferred from
 * polling. Grade is the exception: A/B/F only exist as per-shift counters, so
 * it is attributed from those counters moving over the same poll.
 *
 * Only covers shifts since the worker started recording; for older windows this
 * comes back empty and the per-shift totals are all there is.
 */
export type LamRollEvent = {
  /** The PLC's total roll count at this roll — stable, unique, increasing. */
  roll_seq: number;
  /** Nashville-local naive ISO; when the roll finished. */
  timestamp: string;
  /** That roll's cycle time in seconds, if the PLC recorded one. */
  duration_s: number | null;
  /** Null when the counters and the roll ring disagreed over a poll. */
  grade: "A" | "B" | "F" | null;
  /** False when a poll caught several rolls of differing grades. */
  grade_exact: boolean;
  shift_id: number | null;
};

type RangeParams = {
  start: string;
  end: string;
  group_by?: LamGroupBy;
  /** Adaptive downsampling target (buckets). Omit group_by to use. */
  max_points?: number;
  signal?: AbortSignal;
};

/** The calls for one line, rooted at `prefix` (e.g. "/lam1"). */
export function makeLamApi(prefix: string) {
  return {
    /** Extruder A/B screw speed (Hz) time series. */
    getScrewSpeeds(params: RangeParams) {
      const { signal, ...query } = params;
      return api
        .get<LamSpeedRow[]>(`${prefix}/screw_speeds`, { params: query, signal })
        .then((r) => r.data);
    },

    /** Extruder A/B motor current (A) time series. */
    getMotorCurrents(params: RangeParams) {
      const { signal, ...query } = params;
      return api
        .get<LamCurrentRow[]>(`${prefix}/motor_currents`, { params: query, signal })
        .then((r) => r.data);
    },

    /** Boolean status series (positions, winder, densifier) for the events table. */
    getStatus(params: { start: string; end: string; signal?: AbortSignal }) {
      const { signal, ...query } = params;
      return api
        .get<LamStatusRow[]>(`${prefix}/status`, { params: query, signal })
        .then((r) => r.data);
    },

    /** Latest snapshot: live extruder/winder/densifier state plus fault arrays. */
    getCurrentStatus(signal?: AbortSignal) {
      return api
        .get<LamCurrentStatus>(`${prefix}/currentStatus`, { signal })
        .then((r) => r.data);
    },

    /**
     * Per-shift A/B/F roll counts, oldest-first.
     *
     * Two ways to ask, matching the endpoint. `limit` gives the most recent N
     * shifts, which is what a live chart wants. `start`/`end` — Nashville-local
     * naive ISO, the frame `shift_start` is stored in — give every shift in a
     * window, which is what a report over a quarter wants: a quarter runs to
     * more shifts than `limit` can return, and counting back from now cannot
     * address a window that has already ended.
     */
    getShiftRolls(params?: {
      limit?: number;
      start?: string;
      end?: string;
      signal?: AbortSignal;
    }) {
      const { signal, ...query } = params ?? {};
      return api
        .get<LamShiftRollsRow[]>(`${prefix}/shift_rolls`, { params: query, signal })
        .then((r) => r.data);
    },

    /** One row per roll, at the time the PLC recorded it finishing. */
    getRollEvents(params: { start: string; end: string; signal?: AbortSignal }) {
      const { signal, ...query } = params;
      return api
        .get<LamRollEvent[]>(`${prefix}/roll_events`, { params: query, signal })
        .then((r) => r.data);
    },
  };
}

/** One line's calls, as components that work for either line receive them. */
export type LamApi = ReturnType<typeof makeLamApi>;

/** Which lamination line a line-aware component is showing. */
export type LamLine = "lam1" | "lam2";

/**
 * What the line switch can be set to. "both" is the two lines read as one
 * plant total; it is not a line, so it has no API of its own and never reaches
 * `apiForLine`.
 */
export type LamLineSelection = LamLine | "both";

/** Label for a line, for selectors and headings. */
export const LAM_LINE_LABEL: Record<LamLine, string> = {
  lam1: "Lam 1",
  lam2: "Lam 2",
};

/** Button labels for the line switch — short, because they sit in a toggle. */
export const LAM_LINE_SELECTION_LABEL: Record<LamLineSelection, string> = {
  ...LAM_LINE_LABEL,
  both: "Both",
};

/** Spelled out, for a card heading that has to stand on its own. */
export const LAM_LINE_HEADING: Record<LamLineSelection, string> = {
  lam1: "Lamination 1",
  lam2: "Lamination 2",
  both: "Lamination 1 & 2",
};

/**
 * Whether a selection has roll data worth charting.
 *
 * Both lines do: Lamination 1's PLC clock and shift scheduling were
 * commissioned on 2026-09-22, and lam1-worker has been filling its roll
 * tables since. "both" is still not served — reading the two lines as one
 * plant total needs merging that nothing implements yet, and no card offers
 * the switch — so it keeps the blurred template.
 *
 * The narrowing is the point: it is what lets a caller reach `apiForLine`
 * knowing it holds a line the API actually serves.
 */
export const isLineReady = (line: LamLineSelection): line is LamLine =>
  line !== "both";

/** Why a selection has nothing to show, for the blurred template's caption. */
export const lineNotReadyReason = (_line: LamLineSelection): string =>
  "Lamination 1 & 2 combined isn\u2019t available yet \u2014 pick a single line.";

const LINE_APIS: Record<LamLine, LamApi> = {
  lam1: makeLamApi("/lam1"),
  lam2: makeLamApi("/lam2"),
};

/** The calls for whichever line a component is currently showing. */
export const apiForLine = (line: LamLine): LamApi => LINE_APIS[line];
